import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { ConfirmDialog, Field, KeyText, StatusChip } from "./ui";
import { useSession } from "../auth/AuthProvider";
import { PRODUCTS, productName } from "../config";
import { useLiveQuery } from "../hooks/useLive";
import { customerKeysQuery } from "../lib/queries";
import { CUSTOMER_FIELDS, emptyCustomer } from "../lib/customers";
import { formatDate, licenseStatus } from "../lib/licenses";
import {
  createCustomer,
  deleteCustomer,
  updateCustomer,
} from "../lib/operations";

const pick = (customer) =>
  customer
    ? Object.fromEntries(
        CUSTOMER_FIELDS.map((f) => [f.name, customer[f.name] || ""]),
      )
    : emptyCustomer();

// Create (customer = null) or view/edit a customer record and its keys.
// Admins creating a customer pass `owners` (distributors) to choose who it
// belongs to; "" means a LiFE direct customer.
export default function CustomerDialog({
  open,
  customer,
  distributorId,
  distributorName,
  owners,
  onIssueKey,
  issueLabel = "Issue key for this customer",
  onClose,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const session = useSession();
  const [form, setForm] = useState(pick(customer));
  const [owner, setOwner] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const isNew = !customer;

  // Keys linked to this record. Distributors must filter on their own id.
  const viewerDistributorId =
    session.role === "distributor" ? session.distributorId : null;
  const customerId = open && customer ? customer.id : null;
  const [first, second] = PRODUCTS;
  const keysA = useLiveQuery(
    customerId && `${first.id}/customer/${customerId}`,
    () => customerKeysQuery(first.id, customerId, viewerDistributorId),
    { product: first.id },
  );
  const keysB = useLiveQuery(
    customerId && `${second.id}/customer/${customerId}`,
    () => customerKeysQuery(second.id, customerId, viewerDistributorId),
    { product: second.id },
  );
  const keys = [...keysA.data, ...keysB.data];

  useEffect(() => {
    if (!open) return;
    setForm(pick(customer));
    setOwner(distributorId || "");
    setError(null);
    // Reset when opened or switched to another customer, not on live updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customer?.id]);

  const dirty = CUSTOMER_FIELDS.some(
    (f) => (customer?.[f.name] || "") !== form[f.name],
  );
  const set = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  async function save(event) {
    event.preventDefault();
    if (!form.name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      if (isNew) {
        await createCustomer({
          distributorId: owners ? owner || null : distributorId,
          fields: form,
          actor: session.email,
        });
        onClose();
      } else {
        await updateCustomer(customer.id, form);
      }
    } catch (e) {
      console.error(e);
      setError("Couldn't save the customer. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    try {
      await deleteCustomer(customer.id);
      setConfirmDelete(false);
      onClose();
    } catch (e) {
      console.error(e);
      setError("Couldn't delete the customer.");
      setConfirmDelete(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={busy ? undefined : onClose}
        fullWidth
        maxWidth="sm"
        fullScreen={fullScreen}
      >
        <Box component="form" onSubmit={save}>
          <DialogTitle>{isNew ? "Add customer" : customer.name}</DialogTitle>
          <DialogContent dividers>
            {!isNew && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 2,
                  mb: 2.5,
                }}
              >
                {distributorName && (
                  <Field label="Distributor">{distributorName}</Field>
                )}
                <Field label="Added">{`${formatDate(customer.createdAt)} · ${customer.createdBy || ""}`}</Field>
              </Box>
            )}
            <Box
              sx={{
                display: "grid",
                gap: 1.5,
                pt: 1,
                gridTemplateColumns: { sm: "1fr 1fr" },
              }}
            >
              {isNew && owners && (
                <TextField
                  select
                  label="Customer of"
                  value={owner}
                  onChange={(e) => setOwner(e.target.value)}
                  sx={{ gridColumn: { sm: "1 / -1" } }}
                >
                  <MenuItem value="">LiFE direct</MenuItem>
                  {owners.map((d) => (
                    <MenuItem key={d.id} value={d.id}>
                      {d.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              {CUSTOMER_FIELDS.map((f) => (
                <TextField
                  key={f.name}
                  label={f.label}
                  type={f.type || "text"}
                  value={form[f.name]}
                  onChange={set(f.name)}
                  required={f.required}
                  multiline={f.multiline}
                  minRows={f.multiline ? 2 : undefined}
                  autoFocus={isNew && f.name === "name"}
                  slotProps={{ htmlInput: { maxLength: f.max } }}
                  sx={
                    f.name === "name" || f.multiline
                      ? { gridColumn: { sm: "1 / -1" } }
                      : undefined
                  }
                />
              ))}
            </Box>

            {!isNew && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mt: 3,
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle2">
                    Keys ({keys.length})
                  </Typography>
                  {onIssueKey && (
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => onIssueKey(customer)}
                    >
                      {issueLabel}
                    </Button>
                  )}
                </Box>
                {keys.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No keys linked to this customer yet.
                  </Typography>
                ) : (
                  keys.map((k) => (
                    <Box
                      key={`${k.product}/${k.id}`}
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: 1,
                        py: 0.75,
                        borderTop: 1,
                        borderColor: "divider",
                      }}
                    >
                      <KeyText value={k.licenseKey || k.id} />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ flex: 1 }}
                      >
                        {productName(k.product)} · {k.type}
                      </Typography>
                      <StatusChip status={licenseStatus(k)} />
                    </Box>
                  ))
                )}
              </>
            )}
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 1.5 }}>
            {!isNew && keys.length === 0 && (
              <Button
                color="error"
                onClick={() => setConfirmDelete(true)}
                disabled={busy}
                sx={{ mr: "auto" }}
              >
                Delete
              </Button>
            )}
            <Button color="inherit" onClick={onClose} disabled={busy}>
              {isNew ? "Cancel" : "Close"}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={busy || !form.name.trim() || (!isNew && !dirty)}
            >
              {busy ? "Saving…" : isNew ? "Add customer" : "Save changes"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
      <ConfirmDialog
        open={confirmDelete}
        title="Delete customer?"
        body={`${customer?.name} will be removed from your customer list.`}
        confirmLabel="Delete"
        destructive
        busy={busy}
        onConfirm={remove}
        onClose={() => setConfirmDelete(false)}
      />
    </>
  );
}
